import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { orgToday } from "@/lib/tz";
import { CalendarOff, CalendarX2, Loader2, Plus, Send, Ban, Phone, Info, History } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  Pending: { bg: "bg-amber-500/15", ring: "border-amber-500/30", text: "text-amber-300" },
  Approved: { bg: "bg-emerald-500/15", ring: "border-emerald-500/30", text: "text-emerald-300" },
  Rejected: { bg: "bg-red-500/15", ring: "border-red-500/30", text: "text-red-300" },
  Cancelled: { bg: "bg-slate-500/15", ring: "border-slate-500/30", text: "text-slate-300" },
};
const hm = (t) => { const s = String(t || ""); return s ? s.padStart(8, "0").slice(0, 5) : ""; };
const DOW = ["Fri", "Sat", "Sun", "Mon", "Tue", "Wed", "Thu"];
const slotMode = (m, s, maybe) => {
  const verb = maybe ? "Maybe available" : "Unavailable";
  if (m === "All Day") return `${verb} all day`;
  if (m === "All Day Except Overnight") return maybe ? "Maybe available (day only)" : "Unavailable (day only)";
  return `${verb} ${hm(s.start_time)}–${hm(s.end_time)}`;
};
const fmtDate = (ds) => (ds ? new Date(ds + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "");

const emptyForm = { from_date: "", to_date: "", full_day: true, start_time: "09:00", end_time: "17:00", reason: "" };

export default function WorkerLeave() {
  const [leave, setLeave] = useState([]);
  const [unavail, setUnavail] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState("");
  const [showPast, setShowPast] = useState(false);

  // Upcoming/current leave soonest first; leave whose last day has passed is
  // hidden behind a toggle, most recent first.
  const { upcoming, past } = useMemo(() => {
    const today = orgToday();
    const end = (l) => l.to_date || l.from_date || "";
    const upcoming = leave.filter((l) => end(l) >= today)
      .sort((a, b) => (a.from_date || "").localeCompare(b.from_date || ""));
    const past = leave.filter((l) => end(l) < today)
      .sort((a, b) => (b.from_date || "").localeCompare(a.from_date || ""));
    return { upcoming, past };
  }, [leave]);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.workerLeaveList().then(setLeave).catch(() => setLeave([])),
      api.workerUnavailability().then(setUnavail).catch(() => setUnavail([])),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const notice = useMemo(() => {
    if (!form?.from_date) return null;
    const days = Math.round((new Date(form.from_date + "T00:00:00") - new Date(orgToday() + "T00:00:00")) / 86400000);
    if (days > 30) return { tone: "ok", text: "More than 30 days notice — this will be approved automatically." };
    if (days >= 14) return { tone: "warn", text: "14–30 days notice — we'll try to fill your shifts, but it isn't guaranteed." };
    return { tone: "urgent", text: "Under 14 days notice — filling your shifts isn't guaranteed. Please call the office ASAP after submitting." };
  }, [form?.from_date]);

  const submit = async () => {
    if (!form.from_date) return toast.error("Choose a start date");
    const body = {
      from_date: form.from_date, to_date: form.to_date || form.from_date, full_day: form.full_day,
      start_time: form.full_day ? null : form.start_time + ":00",
      end_time: form.full_day ? null : form.end_time + ":00", reason: form.reason,
    };
    setSaving(true);
    try {
      const res = await api.workerApplyLeave(body);
      if (res.urgency === "urgent") toast.warning(res.message, { duration: 8000 });
      else if (res.status === "Approved") toast.success(res.message);
      else toast.info(res.message, { duration: 6000 });
      setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit leave"); }
    finally { setSaving(false); }
  };

  const cancel = async (name) => {
    setCancelling(name);
    try { await api.workerCancelLeave(name); toast.success("Leave cancelled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
    finally { setCancelling(""); }
  };

  if (loading) return <div className="flex justify-center pt-24"><Loader2 className="h-6 w-6 animate-spin text-[#00c86e]" /></div>;

  return (
    <div className="px-4 pt-5 pb-10" data-testid="worker-leave-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarOff className="h-5 w-5 text-[#00c86e]" />
          <h1 className="text-2xl font-bold text-white tracking-tight font-brand">Leave</h1>
        </div>
        <button onClick={() => setForm({ ...emptyForm })} data-testid="worker-leave-request-btn"
          className="h-9 px-3 rounded-md bg-[#bf3b4f] hover:bg-[#a32f42] text-white text-xs font-semibold flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> Request leave
        </button>
      </div>
      <p className="text-xs text-slate-400 mt-1 mb-5">Request annual leave and track your requests. More notice gives your shifts the best chance of being covered.</p>

      {/* My leave requests */}
      <div className="space-y-2.5" data-testid="worker-leave-list">
        {upcoming.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-slate-400 text-sm" data-testid="worker-leave-empty">
            {past.length ? "No upcoming leave." : "No leave requests yet."} Tap "Request leave" to submit one.
          </div>
        )}
        {[...upcoming, ...(showPast ? past : [])].map((l, i) => {
          const st = STATUS[l.status] || STATUS.Pending;
          const canCancel = l.leave_type !== "Personal" && ["Pending", "Approved"].includes(l.status);
          return (
            <div key={l.name}>
            {i === upcoming.length && (
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 px-1 pt-2 pb-2.5">Past leave</div>
            )}
            <div className={`rounded-2xl border border-white/10 bg-white/5 p-4 ${i >= upcoming.length ? "opacity-70" : ""}`} data-testid={`worker-leave-${l.name}`}>
              <div className="flex items-center justify-between">
                <div className="text-white font-semibold text-[15px]">
                  {fmtDate(l.from_date)}{l.to_date !== l.from_date ? ` – ${fmtDate(l.to_date)}` : ""}
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${st.bg} ${st.ring} ${st.text}`} data-testid={`worker-leave-status-${l.name}`}>{l.status}</span>
              </div>
              <div className="mt-1 text-[11px] text-slate-400">
                {l.leave_type === "Personal" ? "Personal / sick" : "Annual leave"} · {l.full_day ? "All day" : `${hm(l.start_time)}–${hm(l.end_time)}`}
              </div>
              {l.reason && <div className="mt-1 text-[11px] text-slate-300">"{l.reason}"</div>}
              {l.status === "Rejected" && l.reason && <div className="mt-1 text-[11px] text-red-300">Office note: {l.reason}</div>}
              {l.status === "Cancelled" && l.cancel_reason && <div className="mt-1 text-[11px] text-slate-400">{l.cancel_reason}</div>}
              {l.leave_type === "Personal" && (
                <div className="mt-2 text-[10px] text-slate-500 flex items-center gap-1"><Info className="h-3 w-3" /> Managed by the office — contact them to make changes.</div>
              )}
              {canCancel && (
                <button onClick={() => cancel(l.name)} disabled={cancelling === l.name} data-testid={`worker-leave-cancel-${l.name}`}
                  className="mt-3 h-8 w-full rounded-md border border-white/15 text-xs font-semibold text-slate-200 hover:bg-white/10 flex items-center justify-center gap-1 disabled:opacity-60">
                  {cancelling === l.name ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Ban className="h-3.5 w-3.5" /> Cancel this leave</>}
                </button>
              )}
            </div>
            </div>
          );
        })}
        {past.length > 0 && (
          <button onClick={() => setShowPast((v) => !v)} data-testid="worker-leave-toggle-past"
            className="h-9 w-full rounded-md border border-white/10 text-xs font-semibold text-slate-300 hover:bg-white/10 flex items-center justify-center gap-1.5">
            <History className="h-3.5 w-3.5" /> {showPast ? "Hide past leave" : `Show past leave (${past.length})`}
          </button>
        )}
      </div>

      {/* Ongoing unavailability (read-only) */}
      <div className="mt-7">
        <div className="flex items-center gap-2 mb-2">
          <CalendarX2 className="h-4 w-4 text-slate-400" />
          <h2 className="text-base font-semibold text-white">Ongoing availability</h2>
        </div>
        {unavail.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-center text-slate-400 text-sm" data-testid="worker-unavail-empty">
            No ongoing unavailability on file.
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="worker-unavail-list">
            {unavail.map((u) => {
              const maybe = u.availability_type === "Maybe Available";
              return (
              <div key={u.name} className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={`worker-unavail-${u.name}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-white font-semibold text-sm">
                    From {fmtDate(u.effective_from)}{u.end_date ? ` to ${fmtDate(u.end_date)}` : " (ongoing)"}
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${maybe
                    ? "bg-amber-500/15 border-amber-500/30 text-amber-300"
                    : "bg-slate-500/15 border-slate-500/30 text-slate-300"}`}
                    data-testid={`worker-unavail-type-${u.name}`}>
                    {maybe ? "Maybe available" : "Unavailable"}
                  </span>
                </div>
                {maybe && <div className="text-[11px] text-amber-300/80 mt-0.5">The office may call to check if you can work these times.</div>}
                {u.pattern_weeks > 1 && <div className="text-[11px] text-slate-400 mt-0.5">Repeats every {u.pattern_weeks} weeks</div>}
                <div className="mt-2 space-y-1">
                  {(u.slots || []).map((s, i) => (
                    <div key={i} className="text-[11px] text-slate-300">
                      {u.pattern_weeks > 1 ? `Wk${s.week} ` : ""}{DOW[s.day_offset] || "?"}: {slotMode(s.mode, s, maybe)}
                    </div>
                  ))}
                </div>
                {u.notes && <div className="mt-1 text-[11px] text-slate-400">{u.notes}</div>}
              </div>
              );
            })}
            <div className="text-[11px] text-slate-500 flex items-center gap-1 px-1"><Info className="h-3 w-3" /> To change your ongoing availability, please contact the office.</div>
          </div>
        )}
      </div>

      {/* Request leave modal */}
      {form && (
        <div className="fixed inset-0 z-40 bg-black/60 flex items-end sm:items-center justify-center" onClick={() => !saving && setForm(null)}>
          <div className="w-full max-w-lg bg-[#0F1B33] border-t sm:border border-white/10 sm:rounded-2xl rounded-t-2xl p-5 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="worker-leave-form">
            <h3 className="text-lg font-bold text-white mb-3">Request annual leave</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-medium text-slate-400">From</label>
                  <input type="date" value={form.from_date} onChange={(e) => setForm((f) => ({ ...f, from_date: e.target.value, to_date: f.to_date && f.to_date < e.target.value ? e.target.value : f.to_date }))}
                    className="mt-1 h-10 w-full rounded-md bg-white/5 border border-white/15 px-2 text-sm text-white [color-scheme:dark]" data-testid="worker-leave-from" />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-slate-400">To</label>
                  <input type="date" value={form.to_date} min={form.from_date} onChange={(e) => setForm((f) => ({ ...f, to_date: e.target.value }))}
                    className="mt-1 h-10 w-full rounded-md bg-white/5 border border-white/15 px-2 text-sm text-white [color-scheme:dark]" data-testid="worker-leave-to" />
                </div>
              </div>

              <label className="flex items-center justify-between rounded-md border border-white/15 bg-white/5 px-3 py-2.5">
                <span className="text-sm font-medium text-white">Full day</span>
                <input type="checkbox" checked={form.full_day} onChange={(e) => setForm((f) => ({ ...f, full_day: e.target.checked }))}
                  className="h-4 w-4 accent-[#00c86e]" data-testid="worker-leave-fullday" />
              </label>

              {!form.full_day && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-medium text-slate-400">Unavailable from</label>
                    <input type="time" value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                      className="mt-1 h-10 w-full rounded-md bg-white/5 border border-white/15 px-2 text-sm text-white [color-scheme:dark]" data-testid="worker-leave-start" />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-400">Unavailable to</label>
                    <input type="time" value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                      className="mt-1 h-10 w-full rounded-md bg-white/5 border border-white/15 px-2 text-sm text-white [color-scheme:dark]" data-testid="worker-leave-end" />
                  </div>
                </div>
              )}

              <div>
                <label className="text-[11px] font-medium text-slate-400">Reason (optional)</label>
                <textarea value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} rows={3}
                  className="mt-1 w-full rounded-md bg-white/5 border border-white/15 px-2 py-2 text-sm text-white placeholder:text-slate-500" placeholder="Anything the office should know" data-testid="worker-leave-reason" />
              </div>

              {notice && (
                <div className={`rounded-md px-3 py-2.5 text-xs flex items-start gap-2 ${
                  notice.tone === "ok" ? "bg-emerald-500/15 border border-emerald-500/30 text-emerald-200"
                  : notice.tone === "warn" ? "bg-amber-500/15 border border-amber-500/30 text-amber-200"
                  : "bg-red-500/15 border border-red-500/30 text-red-200"}`} data-testid="worker-leave-notice">
                  {notice.tone === "urgent" ? <Phone className="h-4 w-4 shrink-0 mt-px" /> : <Info className="h-4 w-4 shrink-0 mt-px" />}
                  <span>{notice.text}</span>
                </div>
              )}
            </div>

            <div className="mt-5 flex gap-2">
              <button onClick={() => setForm(null)} disabled={saving} className="flex-1 h-11 rounded-md border border-white/15 text-sm font-semibold text-slate-200 hover:bg-white/10">Cancel</button>
              <button onClick={submit} disabled={saving} data-testid="worker-leave-submit"
                className="flex-1 h-11 rounded-md bg-[#bf3b4f] hover:bg-[#a32f42] text-white text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Submit</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
